import React, { useEffect, useState } from 'react';
import {
  Banner,
  Button,
  Cell,
  Counter,
  Div,
  Group,
  Header,
  Input,
  Spinner,
  Text,
  Textarea,
} from '@vkontakte/vkui';
import {
  Icon20Check,
  Icon20ClockOutline,
  Icon20DoorEnterArrowRightOutline,
  Icon20NotificationOutline,
  Icon20Play,
  Icon20RefreshOutline,
  Icon28GlobeOutline,
} from '@vkontakte/icons';


const formatEventTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};


export const SharedWorldMode = ({
  status,
  loading,
  actionLoading,
  error,
  onRefresh,
  onContinue,
  onEnter,
  onLeaveEvent,
  onAcknowledge,
}) => {
  const [setup, setSetup] = useState(null);
  const [username, setUsername] = useState('Пользователь');
  const [userDescription, setUserDescription] = useState('');

  useEffect(() => {
    if (status?.binding) setSetup(null);
  }, [status?.binding?.character_id, status?.binding?.event_id]);

  const beginSetup = (eventId = null) => {
    setSetup({ eventId });
    setUsername('Пользователь');
    setUserDescription('');
  };

  const submitEntry = async () => {
    if (!setup || !username.trim()) return;
    await onEnter({
      characterId: status?.world?.entry_character_id || 'World',
      eventId: setup.eventId || null,
      username: username.trim(),
      userDescription: userDescription.trim(),
    });
  };

  const joinWithCurrentCharacter = async (eventId) => {
    const characterId = status?.binding?.character_id;
    if (!characterId) return;
    await onEnter({
      characterId,
      eventId,
      username: status?.binding?.username || 'Пользователь',
      userDescription: status?.binding?.user_description || '',
    });
  };

  if (loading && !status) {
    return (
      <Group>
        <Div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
          <Spinner size="medium" />
        </Div>
      </Group>
    );
  }

  if (!status?.enabled) return null;

  const activeBindingEvent = status.events?.find(
    (event) => event.event_id === status.binding?.event_id,
  );
  const selectedEvent = status.events?.find(
    (event) => event.event_id === setup?.eventId,
  );

  return (
    <>
      <Group>
        <Div style={{ paddingTop: 14, paddingBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <Icon28GlobeOutline
              width={36}
              height={36}
              style={{ color: 'var(--vkui--color_icon_accent)', flexShrink: 0 }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <Text weight="semibold" style={{ display: 'block', fontSize: 20 }}>
                {status.world.name}
              </Text>
              <Text
                style={{
                  display: 'block',
                  marginTop: 3,
                  color: 'var(--vkui--color_text_secondary)',
                  fontSize: 13,
                }}
              >
                Сложность: {status.world.difficulty_label}
              </Text>
            </div>
            <Button
              mode="tertiary"
              size="s"
              aria-label="Обновить состояние мира"
              title="Обновить состояние мира"
              before={<Icon20RefreshOutline />}
              loading={loading}
              onClick={onRefresh}
            />
          </div>
          <Text
            style={{
              display: 'block',
              marginTop: 12,
              color: 'var(--vkui--color_text_secondary)',
              lineHeight: 1.4,
              fontSize: 14,
            }}
          >
            {status.world.description}
          </Text>
        </Div>
      </Group>

      {error && (
        <Group>
          <Banner title="Общий мир недоступен" subtitle={error} />
        </Group>
      )}

      {(status.notifications || []).length > 0 && (
        <Group header={<Header mode="secondary">Новости мира</Header>}>
          {status.notifications.map((notification) => (
            <Banner
              key={notification.notification_id}
              before={<Icon20NotificationOutline />}
              title={notification.title || 'Новость мира'}
              subtitle={notification.message}
              actions={(
                <Button
                  size="s"
                  mode="secondary"
                  before={<Icon20Check />}
                  onClick={() => onAcknowledge(notification.notification_id)}
                >
                  Прочитано
                </Button>
              )}
            />
          ))}
        </Group>
      )}

      {status.binding && (
        <Group header={<Header mode="secondary">Ваш герой</Header>}>
          <Cell
            before={<Icon28GlobeOutline width={42} height={42} />}
            subtitle={(
              activeBindingEvent?.status === 'scheduled'
                ? `Записан: ${activeBindingEvent.title}`
                : activeBindingEvent?.title || 'Свободная игра'
            )}
            after={<Counter mode="primary">в мире</Counter>}
          >
            {status.binding.username || 'Герой'}
          </Cell>
          <Div style={{ paddingTop: 4 }}>
            <Button
              stretched
              size="l"
              before={<Icon20Play />}
              onClick={onContinue}
            >
              {activeBindingEvent?.status === 'active'
                ? 'Продолжить событие'
                : 'Продолжить свободную игру'}
            </Button>
          </Div>
        </Group>
      )}

      {!status.binding && !setup && (
        <Group header={<Header mode="secondary">Свободная игра</Header>}>
          <Div>
            <Text
              style={{
                display: 'block',
                color: 'var(--vkui--color_text_secondary)',
                lineHeight: 1.4,
                marginBottom: 12,
              }}
            >
              Хогвартс живёт в реальном времени, даже когда ты не участвуешь в событии.
            </Text>
            <Button
              stretched
              size="l"
              mode="secondary"
              before={<Icon20DoorEnterArrowRightOutline />}
              onClick={() => beginSetup(null)}
            >
              Войти в свободную игру
            </Button>
          </Div>
        </Group>
      )}

      {setup && (
        <Group
          header={(
            <Header mode="secondary">
              {selectedEvent?.status === 'scheduled'
                ? `Запись: ${selectedEvent.title}`
                : selectedEvent
                ? `Вход: ${selectedEvent.title}`
                : 'Вход в общий мир'}
            </Header>
          )}
        >
          <Div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Text style={{ color: 'var(--vkui--color_text_secondary)', lineHeight: 1.4 }}>
                Создай собственного героя. Персонажи Хогвартса останутся NPC,
                которых можно встретить по ходу истории.
              </Text>
              <Input
                value={username}
                maxLength={50}
                placeholder="Имя твоего персонажа"
                onChange={(event) => setUsername(event.target.value)}
              />
              <Textarea
                value={userDescription}
                maxLength={500}
                rows={3}
                placeholder="Краткое описание твоего персонажа"
                onChange={(event) => setUserDescription(event.target.value)}
              />
              <Button
                stretched
                size="l"
                loading={actionLoading}
                disabled={!username.trim() || actionLoading}
                before={<Icon20DoorEnterArrowRightOutline />}
                onClick={submitEntry}
              >
                {selectedEvent?.status === 'scheduled' ? 'Создать героя и записаться' : 'Войти в мир'}
              </Button>
              <Button
                stretched
                mode="tertiary"
                disabled={actionLoading}
                onClick={() => setSetup(null)}
              >
                Отмена
              </Button>
            </Div>
        </Group>
      )}

      <Group header={<Header mode="secondary">События</Header>}>
        {(status.events || []).length === 0 && (
          <Div>
            <Text style={{ color: 'var(--vkui--color_text_secondary)' }}>
              Активных и запланированных событий пока нет.
            </Text>
          </Div>
        )}
        {(status.events || []).map((event) => {
          const isCurrent = status.binding?.event_id === event.event_id;
          const canJoinWithBinding = (
            status.binding
            && !status.binding.event_id
            && event.actionable
          );
          return (
            <Div
              key={event.event_id}
              style={{
                paddingTop: 12,
                paddingBottom: 12,
                borderBottom: '1px solid var(--vkui--color_separator_secondary)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text weight="semibold" style={{ display: 'block', fontSize: 16 }}>
                    {event.title}
                  </Text>
                  <Text
                    style={{
                      display: 'block',
                      marginTop: 5,
                      color: 'var(--vkui--color_text_secondary)',
                      lineHeight: 1.35,
                      fontSize: 14,
                    }}
                  >
                    {event.description}
                  </Text>
                </div>
                <Counter mode={event.status === 'active' ? 'primary' : 'secondary'}>
                  {event.status === 'active' ? 'идёт' : 'скоро'}
                </Counter>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <Text
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    color: 'var(--vkui--color_text_secondary)',
                    fontSize: 13,
                  }}
                >
                  <Icon20ClockOutline width={18} height={18} />
                  {formatEventTime(event.starts_at)} – {formatEventTime(event.ends_at)}
                </Text>
                <Text style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>
                  Свободно: {event.available_places} из {event.max_players}
                </Text>
              </div>
              {event.role && (
                <Text style={{ display: 'block', marginTop: 8, fontSize: 13 }}>
                  Роль: {event.role}
                </Text>
              )}
              {event.objective && (
                <Text style={{ display: 'block', marginTop: 4, fontSize: 13 }}>
                  Цель: {event.objective}
                </Text>
              )}
              {isCurrent && (
                <>
                  {event.status === 'active' && (
                    <Button
                      stretched
                      size="m"
                      style={{ marginTop: 12 }}
                      before={<Icon20Play />}
                      onClick={onContinue}
                    >
                      Продолжить событие
                    </Button>
                  )}
                  <Button
                    stretched
                    size="m"
                    mode="secondary"
                    style={{ marginTop: 8 }}
                    loading={actionLoading}
                    before={<Icon20DoorEnterArrowRightOutline />}
                    onClick={() => onLeaveEvent(event.event_id)}
                  >
                    {event.status === 'scheduled' ? 'Отменить запись' : 'Покинуть событие'}
                  </Button>
                </>
              )}
              {!status.binding && event.actionable && !setup && (
                <Button
                  stretched
                  size="m"
                  mode="secondary"
                  style={{ marginTop: 12 }}
                  before={<Icon20DoorEnterArrowRightOutline />}
                  onClick={() => beginSetup(event.event_id)}
                >
                  {event.status === 'scheduled' ? 'Записаться' : 'Войти в событие'}
                </Button>
              )}
              {canJoinWithBinding && (
                <Button
                  stretched
                  size="m"
                  mode="secondary"
                  style={{ marginTop: 12 }}
                  loading={actionLoading}
                  before={<Icon20DoorEnterArrowRightOutline />}
                  onClick={() => joinWithCurrentCharacter(event.event_id)}
                >
                  {event.status === 'scheduled' ? 'Записаться' : 'Присоединиться'}
                </Button>
              )}
            </Div>
          );
        })}
      </Group>
    </>
  );
};
